/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 * App fetch request controller
 *
 * @author Mikhail Kokoulin
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.11.0
 * @since 1.0.0
 */

import { IErrorResponse, IResponse } from '../../sagas/requestSaga';

export interface IResponseMeta {
    appLocation: string | null;
    cached: boolean;
    processed: boolean;
    unauthorized: boolean;
    version: string | null;
}

/**
 * The method check status and return parsed Json if status !== (204 || 205)
 */
export function parse(response: Response): Promise<IResponse | IResponseMeta> {

    const appLocation = response.headers.get('location');

    const meta: IResponseMeta = {
        appLocation: response.headers.get('location'),
        cached: !appLocation ? true : appLocation.indexOf('waitLogin') !== 0,
        processed: response.status === 204,
        unauthorized: response.status === 401,
        version: response.headers.get('UI-Version'),
    };

    if (response.status === 204 || response.status === 205) {
        return Promise.resolve({ ...meta });
    }
    return response.json().then(({ data, error }: any) => {
        return {
            data,
            error,
            ...meta,
        };
    }).catch(() => {
        return { ...meta };
    });
}

/**
 * The method check status
 * If status > 200 && < 302 return resolve(response) else return reject(error)
 * @param response Response
 * @return {response: Response | error: Error}
 */
export function checkStatus(response: Response): any {
    if ((response.status >= 200 && response.status < 302) || response.status === 401) {
        return response;
    }
    const error: IErrorResponse = new Error(response.statusText);
    error.code = response.status;
    return response.json()
        .catch(() => {
            error.response = {
                data: null,
                error: {
                    code: 'unknown.error',
                    message: 'Unable to get error from response',
                },
            };
            throw error;
        })
        .then((data) => {
            error.response = data;
            throw error;
        })
}

export interface IFetchOptions {
    abortControllerId?: string;
}

interface ICurrentRequests {
    [url: string]: {
        abortController: AbortController;
    };
}

const currentRequests: ICurrentRequests = {};

/**
 * The method gets requests and processes the response.
 * @param url
 * @param options
 * @param fetchOptions
 * @return {promise: Promise}
 */
export default function appRequest(url: string, options: any, fetchOptions: IFetchOptions = {}): Promise<any> {

    if (fetchOptions.abortControllerId) {
        if (currentRequests[fetchOptions.abortControllerId]?.abortController) {
            currentRequests[fetchOptions.abortControllerId].abortController.abort();
        }
        currentRequests[fetchOptions.abortControllerId] = {
            abortController: new AbortController(),
        };
        options.signal = currentRequests[fetchOptions.abortControllerId].abortController.signal;
    }

    return fetch(url, options)
        .then(response => {
            fetchOptions.abortControllerId && delete currentRequests[fetchOptions.abortControllerId];
            return response;
        })
        .then(checkStatus)
        .then(parse)
        .catch((error: any) => {
            if (error.name !== 'AbortError') {
                fetchOptions.abortControllerId && delete currentRequests[fetchOptions.abortControllerId];
            }
            process.env.NODE_ENV !== 'production' && console.error('Fetch error', error);
            throw error;
        });
};

// for requests with redux-toolkit
export function appRequestV2(
    input: RequestInfo,
    init?: RequestInit,
    fetchOptions: IFetchOptions = {},
): Promise<Response> {

    if (fetchOptions.abortControllerId) {
        if (currentRequests[fetchOptions.abortControllerId]?.abortController) {
            currentRequests[fetchOptions.abortControllerId].abortController.abort();
        }
        currentRequests[fetchOptions.abortControllerId] = {
            abortController: new AbortController(),
        };
        if (init) init.signal = currentRequests[fetchOptions.abortControllerId].abortController.signal;
    }

    return fetch(input, init)
        .then(response => {
            fetchOptions.abortControllerId && delete currentRequests[fetchOptions.abortControllerId];
            return response;
        })
        .catch((error: any) => {
            if (error.name !== 'AbortError') {
                fetchOptions.abortControllerId && delete currentRequests[fetchOptions.abortControllerId];
            }
            process.env.NODE_ENV !== 'production' && console.error('Fetch error', error);
            throw error;
        });
}

export function appRequestDownloadFile(url: string): Promise<any> {
    const options: RequestInit = {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Pragma': 'no-cache',
        },
        mode: 'cors',
    };

    return fetch(url, options)
        .then(checkStatus)
        .then((response) => {
            const filename = response.headers.get('filename');
            response.blob()
                .then((blob: Blob) => {
                    //https://blog.jayway.com/2017/07/13/open-pdf-downloaded-api-javascript/
                    // IE doesn't allow using a blob object directly as link href
                    // instead it is necessary to use msSaveOrOpenBlob
                    // @ts-ignore
                    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                        // @ts-ignore
                        window.navigator.msSaveOrOpenBlob(blob, filename);
                        return;
                    }

                    // 2. Create blob link to download
                    const url = window.URL.createObjectURL(new Blob([blob]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = filename;
                    // 3. Append to html page
                    document.body.appendChild(link);
                    // 4. Force download
                    link.click();
                    // 5. Clean up and remove the link
                    link.parentNode && link.parentNode.removeChild(link);
                });
        })
        .catch((error: any) => {
            console.error('Fetch error', error);
            throw error;
        });
}

export function getResponseFromError(error: any) {
    if (!error.response) {
        return Promise.resolve();
    }
    return error.response;
}
